import { ChangeDetectionStrategy, ChangeDetectorRef, Component, QueryList, ViewChildren } from "@angular/core";
import { List } from "immutable";
import { MessagingService } from "../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../core/messaging/severity-type.enum";
import { ListItem } from "../../../../shared/list/list-item";
import { IOnUploadNew, UploadNewComponent } from "../../../../shared/upload/upload-new.component";
import { BulkProjectUploadService } from "./bulk-project-upload.service";

const BULK_PROJECT_FILE_TYPE = "BulkProject";
const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

@Component({
  selector: "app-bulk-project-upload",
  templateUrl: "./bulk-project-upload.component.html",
  styleUrls: ["./bulk-project-upload.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkProjectUploadComponent {

  fileName: string;
  fileLocation: string;
  isFileUploaded = false;
  isValidated = false;
  isProjectValidationPassed = false;
  isProcessingVisible = false;
  processingHeaderText: string;
  validationIssueData: string;
  summaryReport = List<ListItem>();
  @ViewChildren("appFileUpload") filesToUpload: QueryList<UploadNewComponent>;

  constructor(
    private bulkProjectUploadService: BulkProjectUploadService,
    private messagingService: MessagingService,
    private changeDetector: ChangeDetectorRef
  ) { }

  get summaryHeader(): string {
    return "VALIDATION SUMMARY";
  }

  get validationStatusMessage(): string {
    return this.isProjectValidationPassed ? "PASSED" : "FAILED";
  }

  get hasPassed(): boolean {
    return this.isProjectValidationPassed;
  }

  trackByIndex(index) {
    return index;
  }

  uploadDocument(event: IOnUploadNew): void {
    const file = event.formData.getAll("Document")[0] as File;

    if (!file) {
      return;
    }

    this.processingHeaderText = "PLEASE WAIT...UPLOAD IS IN PROGRESS";
    this.isProcessingVisible = true;
    this.changeDetector.markForCheck();

    const payload = {
      name: file.name,
      type: file.type || XLSX_MIME_TYPE,
      fileType: BULK_PROJECT_FILE_TYPE,
    };

    this.bulkProjectUploadService.getPresignedURL(JSON.stringify(payload)).subscribe({
      next: response => {
        if (response && response.presignedURL) {
          this.bulkProjectUploadService.uploadFile(response.presignedURL, file)
            .then(() => {
              this.fileName = file.name;
              this.fileLocation = response.fileName;
              this.isFileUploaded = true;
              this.isProcessingVisible = false;
              this.messagingService.showToast("File uploaded successfully.", SeverityType.SUCCESS);
              this.changeDetector.markForCheck();
            })
            .catch(() => {
              this.onUploadError();
            });
        } else {
          this.onUploadError();
        }
      },
      error: () => {
        this.onUploadError();
      },
    });
  }

  validate(): void {
    if (!this.isFileUploaded) {
      this.messagingService.showToast("Please upload an .xlsx file.", SeverityType.ERROR);
      return;
    }

    this.processingHeaderText = "PLEASE WAIT...VALIDATION IS IN PROGRESS";
    this.isProcessingVisible = true;

    this.bulkProjectUploadService.bulkValidate(this.fileLocation).subscribe(
      response => {
        this.validationIssueData = response.body.fileContent;
        this.isProjectValidationPassed = response.body.totalProjectsFailed === 0;

        this.summaryReport = List<ListItem>();
        this.summaryReport = this.summaryReport.push(
          new ListItem({ key: "Total Projects Passed", value: response.body.totalProjectsPassed.toString(), url: "" }),
          new ListItem({ key: "Total Projects Failed", value: response.body.totalProjectsFailed.toString(), url: "" }),
          new ListItem({ key: "Validation Status", value: this.validationStatusMessage, url: "" })
        );

        this.isValidated = true;
        this.isProcessingVisible = false;
        this.changeDetector.markForCheck();
      },
      () => {
        this.isProcessingVisible = false;
        this.messagingService.showToast("Please try again", SeverityType.ERROR);
        this.changeDetector.markForCheck();
      });
  }

  reset(): void {
    this.filesToUpload.forEach(fileToUpload => {
      fileToUpload.clearUpload();
    });
    this.fileName = null;
    this.fileLocation = null;
    this.isFileUploaded = false;
    this.isValidated = false;
    this.isProjectValidationPassed = false;
    this.summaryReport = List<ListItem>();
    this.changeDetector.markForCheck();
  }

  downloadValidationIssues(): void {
    const blob = new Blob([this.validationIssueData], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "validation-issues.csv";
    a.click();
    a.remove();
  }

  getValidationMessageClass(value: string): string {
    switch (value) {
      case "FAILED":
        return "summary-body__details--danger";
      case "PASSED":
        return "summary-body__details--pass";
      default:
        return "summary-body__details";
    }
  }

  private onUploadError(): void {
    this.isProcessingVisible = false;
    this.messagingService.showToast("Error uploading file using pre-signed URL, please try again.", SeverityType.ERROR);
    this.reset();
  }
}
